import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, Package, X } from 'lucide-react';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function HSCodeSelect({ value, onChange, placeholder = "Search HS codes...", className = "" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [selectedCode, setSelectedCode] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    // Handle click outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (value && value.code) {
      setSelectedCode(value);
    }
  }, [value]);

  const searchHSCodes = async (query) => {
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/reference/hs-codes?q=${encodeURIComponent(query)}&limit=15`);
      setResults(response.data);
    } catch (error) {
      console.error('Failed to search HS codes:', error);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearch(query);
    setIsOpen(true);

    // Debounce search
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      searchHSCodes(query);
    }, 300);
  };

  const handleSelect = (item) => {
    setSelectedCode(item);
    onChange(item.code, item.description);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedCode(null);
    onChange('', '');
    setSearch('');
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {selectedCode ? (
        <div className="flex items-center justify-between w-full px-3 py-2 text-sm bg-background border rounded-md">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Badge variant="secondary" className="shrink-0">{selectedCode.code}</Badge>
            <span className="truncate text-sm">{selectedCode.description}</span>
          </div>
          <button
            onClick={handleClear}
            className="ml-2 p-1 hover:bg-accent rounded"
            data-testid="hs-code-clear"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={placeholder}
            value={search}
            onChange={handleSearchChange}
            onFocus={() => search.length >= 2 && setIsOpen(true)}
            className="pl-9"
            data-testid="hs-code-search"
          />
        </div>
      )}

      {isOpen && (search.length >= 2 || results.length > 0) && (
        <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-64 overflow-hidden">
          {isLoading ? (
            <div className="p-3 text-sm text-muted-foreground text-center">Searching...</div>
          ) : results.length === 0 ? (
            <div className="p-3 text-sm text-muted-foreground text-center">
              {search.length < 2 ? 'Type at least 2 characters...' : 'No HS codes found'}
            </div>
          ) : (
            <div className="overflow-y-auto max-h-64">
              {results.map((item, idx) => (
                <div
                  key={`${item.code}-${idx}`}
                  className="flex items-start gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-accent transition-colors border-b last:border-b-0"
                  onClick={() => handleSelect(item)}
                  data-testid={`hs-code-option-${item.code}`}
                >
                  <Badge variant="outline" className="shrink-0 mt-0.5">{item.code}</Badge>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm leading-tight">{item.description}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Chapter {item.chapter}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
